import React, { useRef, useEffect, useState } from 'react';
import D3Funnel from 'd3-funnel';

interface FunnelProps {
  data: any;
}

const Funnel3DChart = React.forwardRef<HTMLDivElement, FunnelProps>(({ data }, ref) => {
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  const [isMobile, setIsMobile] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<any>(null);

  // Responsive options based on screen size
  const getResponsiveOptions = () => {
    const screenWidth = window.innerWidth;
    const isMobileView = screenWidth < 768;
    const isTabletView = screenWidth > 768 && screenWidth <= 1024;

    return {
      block: {
        dynamicHeight: false,
        minHeight: isMobileView ? 12 : 15,
        fill: { type: 'gradient' },
        highlight: true,
      },
      chart: {
        pinch: true,
        bottomWidth: isMobileView ? 0.6 : 0.5,
        animate: 0,
        curve: { enabled: true }, // Disable curve on mobile for better performance
        bottomPinch: true,
        width: dimensions.width || undefined,
        height: dimensions.height || undefined,
      },
      tooltip: {
        enabled: false,
        format: (label: string, value: number) => {
          const dvalue = data.find((d: any) => d[0] === label);
          return `${label}: ${dvalue?.length > 3 ? dvalue[3] : value}`;
        },
      },
      label: {
        enabled: true,
        fontSize: isMobileView ? '10px' :  '12px',
        fontFamily: 'inherit',
        format: (label: string, value: number) => {
          const dvalue = data.find((d: any) => d[0] === label);
          // Handle multi-line labels on mobile
          if (isMobileView && label.length > 15) {
            const words = label.split(' ');
            const mid = Math.ceil(words.length / 2);
            const firstLine = words.slice(0, mid).join(' ');
            const secondLine = words.slice(mid).join(' ');
            return `${firstLine}\n${secondLine}\n$${dvalue?.length > 3 ? dvalue[3] : value}`;
          }
          return `${label}\n ${dvalue?.length > 3 ? dvalue[3] : value}`;
        },
      },
    };
  };

  // Handle resize
  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      setIsMobile(width <= 768);

      if (containerRef.current) {
        const containerWidth = containerRef.current.clientWidth;

        // Responsive height calculation
        let height;
        if (width <= 576) {
          height = 300; // Mobile small
        } else if (width <= 768) {
          height = 350; // Mobile large
        } else if (width <= 1024) {
          height = 400; // Tablet
        } else {
          height = 450; // Desktop
        }

        setDimensions({
          width: containerWidth,
          height: height,
        });
      }
    };

    handleResize(); // Initial call
    window.addEventListener('resize', handleResize);

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Draw/redraw chart when data or dimensions change
  useEffect(() => {
    if (!containerRef.current || dimensions.width === 0) return;

    // Clear previous chart
    if (containerRef.current) {
      containerRef.current.innerHTML = '';
    }

    const options = getResponsiveOptions();
    const chart = new D3Funnel(containerRef.current);
    chart.draw(data, options);
    chartRef.current = chart;

    return () => {
      if (containerRef.current) {
        containerRef.current.innerHTML = '';
      }
    };
  }, [data, dimensions]);

  return (
    <div
      className="container-fluid p-0 position-relative"
      style={{
        width: '100%',
        minHeight: isMobile ? '300px' : '450px',
        height: 'auto',
      }}
    >
      {/* Responsive container with Bootstrap classes */}
      <div
        ref={(node) => {
          containerRef.current = node;
          if (typeof ref === 'function') {
            ref(node);
            return;
          }
          if (ref) {
            ref.current = node;
          }
        }}
        className="w-100 h-100 d-flex justify-content-center align-items-center"
        style={{
          position: 'relative',
          minHeight: '300px',
          maxHeight: '500px',
          overflow: 'visible',
        }}
      />

      {/* Optional: Add loading state or fallback */}
      {dimensions.width === 0 && (
        <div className="position-absolute top-50 start-50 translate-middle">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      )}
    </div>
  );
});

export default Funnel3DChart;